/**
 * Which OS the app is running on, answered synchronously.
 *
 * Tauri exposes the platform through an async `invoke`, which is fine for
 * setup work but not for hot paths: the agent-voice speak path has to pick an
 * engine without an await, or every spoken sentence pays an IPC round-trip.
 *
 * `detectOS()` is the shared user-agent parser, but it returns `null` outside a
 * real browser UA — which includes jsdom under vitest. These helpers add the
 * `navigator.platform` check that our own webviews always answer exactly
 * (WebView2 reports `Win32`, WKWebView reports `MacIntel` on both Intel and
 * Apple Silicon), and fall back to the UA parser otherwise.
 *
 * The platform string is matched at its START, deliberately. A substring test
 * for "win" also matches "darwin", which is what jsdom puts in its user agent
 * on macOS — so a loose check reports every macOS test run as Windows.
 */
import { detectOS } from "./detect-os";

function platformString(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.platform || "";
}

/** True on Windows (any version, any architecture). */
export function isWindowsPlatform(): boolean {
  const platform = platformString();
  if (platform) return /^win/i.test(platform);
  return detectOS() === "windows";
}

/** True on macOS. Note that Apple Silicon also reports `MacIntel`, so this
 *  says nothing about the CPU. */
export function isMacPlatform(): boolean {
  const platform = platformString();
  if (platform) return /^mac/i.test(platform);
  return detectOS() === "macos";
}
